import { createMachine, assign, sendParent } from 'xstate';
import * as FlexLayout from 'flexlayout-react';
import { model, modelSmall } from '../../utils/panelHelper';
import logger from '../../logger';

/**
 * Collect every tab component name present in the default model's first tabset
 * that should always exist. If the saved cloud model is missing any of them,
 * append them so users who saved their layout before a new tab was added still
 * see the new tab without having to reset their layout.
 */
const REQUIRED_SIDEBAR_COMPONENTS = ['history'];

/**
 * Migrate a saved FlexLayout model JSON to use current component names.
 * Renames any tab whose component was saved under a legacy name to the
 * canonical current name so that getFactory and URL-sync continue to work.
 *
 * Backwards compatibility: 'noteTabsMobile' was renamed to 'openNotesPanel'.
 * Cloud-persisted models saved before the rename will still carry the old name.
 */
const LEGACY_COMPONENT_NAMES = {
  noteTabsMobile: 'openNotesPanel',
};

export function migrateComponentNames(node) {
  if (!node) return;
  if (node.type === 'tab' && node.component && LEGACY_COMPONENT_NAMES[node.component]) {
    const oldName = node.component;
    node.component = LEGACY_COMPONENT_NAMES[node.component];
    logger.info({ oldName, newName: node.component }, 'dryChromeState: migrated legacy component name');
  }
  if (Array.isArray(node.children)) {
    node.children.forEach(migrateComponentNames);
  }
}

function injectMissingTabs(savedModelJson) {
  try {
    const json = typeof savedModelJson === 'string'
      ? JSON.parse(savedModelJson)
      : JSON.parse(JSON.stringify(savedModelJson)); // deep clone

    // Migrate any legacy component names before further processing
    migrateComponentNames(json?.layout);

    const rows = json?.layout?.children;
    if (!Array.isArray(rows)) return savedModelJson;

    // First tabset in the row is the sidebar
    const sidebarTabset = rows.find((n) => n.type === 'tabset');
    if (!sidebarTabset || !Array.isArray(sidebarTabset.children)) return savedModelJson;

    const existing = new Set(sidebarTabset.children.map((t) => t.component));
    for (const component of REQUIRED_SIDEBAR_COMPONENTS) {
      if (!existing.has(component)) {
        // Pull the default tab definition from the default model
        const defaultTabset = model?.layout?.children?.find((n) => n.type === 'tabset');
        const defaultTab = defaultTabset?.children?.find((t) => t.component === component);
        sidebarTabset.children.push(defaultTab ?? { type: 'tab', name: component, component });
        logger.info({ component }, 'dryChromeState: injected missing tab into saved model');
      }
    }
    return json;
  } catch (e) {
    logger.warn({ e }, 'dryChromeState: injectMissingTabs failed, using original model');
    return savedModelJson;
  }
}

/*
 * READ: getChromeStateJSON
 * READ: getChromeStateString
 * READ: getModel
 * READ: getModelFromLocal
 * UPDATE: setModel
 * HELPER: setDebugger
 * HELPER: selectTab
 */

/*
* Tasks:
[ ] RequiredAppDataState sends event to DryChromeMachine when the active model is set. May need
    OR should the DryChromeMachine be proactive and reach out for the data whenever it is instantiated?
    The state may or may not be there, but it won't be blocked
[ ] Model is loaded into the flex layout
[ ] Changes on Flex Layout save back up to the parent appState on ModelChanges
*/
export const DryChromeMachine = createMachine(
  {
    id: 'DryChromeMachine',
    predictableActionArguments: true,

    // https://xstate.js.org/docs/guides/context.html#action-order
    preserveActionOrder: true,
    initial: 'initialize',
    context: {
      activeChromeModel: null, // DryChrome enriched FlexLayout model stored as JSON
      activeFlexLayoutModel: null, // Current active RAW FlexLayoout Model Tree
      chromeModels: null, // DryChrome enriched FlexLayout models stored as JSON
      flexLayoutModelMobile: null, // mobile RAW FlexLayoout Model Tree (may/may not be the current active model)
      breakpoint: null, // Current active breakpoint
    },
    states: {
      initialize: {
        /*
         * Entry will fire multiple times before there is context that it can utilize
         * In order to prevent propagating to the next state, a guard is added.
         */
        entry: (context, event) => {
          logger.info('DryChromeMachine Initialized!', context, event);
          return {};
        },
        always: [
          {
            target: 'Ready',
            // cond: 'hasChromeModels',
          },
        ],
        on: {
          GetChromeModel: {
            actions: (context, event) => {
              logger.info({ context, event }, 'GetChromeModel');
              return {};
            },
          },
        },
      },
      Ready: {
        entry: ['welcome'],
        on: {
          SetModel: {
            actions: [
              assign((context, event) => {
                logger.info({ context, event }, 'DryChromeState - SetModel');
                const { model, breakpoint } = event.data || event;

                return {
                  activeFlexLayoutModel: model,
                  breakpoint: breakpoint,
                };
              }),
              // Send the updated model to parent app state
              sendParent((context, event) => {
                const { model } = event.data || event;
                return {
                  type: 'SetChromeModelFromChild',
                  activeFlexLayoutModel: model,
                };
              }),
            ],
          },
          SelectTab: {
            actions: assign((context, event) => {
              const tabName = event.tabName;
              const model = context.activeFlexLayoutModel;

              if (!model) {
                logger.warn('SelectTab: no activeFlexLayoutModel in context');
                return {};
              }

              // Find the tab node with the matching component name
              let targetId = null;
              model.visitNodes((node) => {
                if (node.getType() === 'tab' && node.getComponent() === tabName) {
                  targetId = node.getId();
                }
              });

              if (!targetId) {
                logger.warn('SelectTab: no tab found for component', tabName);
                return {};
              }

              model.doAction(FlexLayout.Actions.selectTab(targetId));
              return { activeFlexLayoutModel: model };
            }),
          },
          ActionReceiver: {
            actions: (context, event) => {
              const action = event.action;
              logger.debug('actionReceiver', action);
              return action;
            },
          },
          GetModel: {
            actions: ['getContext'],
          },
          GetChromeModelFromReady: {
            actions: (context, event) => {
              logger.info({ context, event }, 'GetChromeModel');
              return {
                activeFlexLayoutModel: context.activeFlexLayoutModel,
              };
            },
          },
          SetChromeModel: {
            actions: [
              assign((context, event) => {
                logger.info(
                  {
                    context,
                    event,
                  },
                  'DryChromeMachine: setActiveChromeModel'
                );
                let activeChromeModel = null;
                const { breakpoint, chromeModels } = event;

                // Debug logging to see what's actually being passed
                logger.info('SetChromeModel event data:', {
                  breakpoint,
                  chromeModels,
                  eventType: typeof chromeModels,
                  isArray: Array.isArray(chromeModels),
                });

                // Add defensive check to ensure chromeModels is an array before using .find()
                if (!Array.isArray(chromeModels)) {
                  logger.warn('chromeModels is not an array:', chromeModels);
                  // Return early with default values to prevent errors
                  return {
                    activeChromeModel: null,
                    activeFlexLayoutModel: null,
                    breakpoint: event?.breakpoint,
                    chromeModels: chromeModels,
                  };
                }

                /*
                 * Consolidate the logic for matching a breakpoint to a specific chrome model
                 * Reasons for separate chromeModels
                 * 1. Retrieve the chrome model based on breakpoint
                 * 2. Control the font-size based on the application dimensions/breakpoint
                 */
                switch (breakpoint) {
                  case 'desktopLarge':
                  case 'desktopWide':
                  case 'desktop':
                    activeChromeModel = chromeModels.find(
                      (element) => element.modelId === 'dryChromeClass_v2'
                    );
                    break;
                  case 'mobile':
                  case 'tabletLandscape':
                  case 'tablet':
                  case 'mobileLandscape':
                  default:
                    activeChromeModel = chromeModels.find(
                      (element) => element.modelId === 'dryChromeClassSmall_v2'
                    );
                    break;
                }

                /*
                 * DryChromeModel JSON to native FlexLayout Model
                 */
                // const dryChromeModelToFlexModel = FlexLayout.Model.fromJson(
                //   activeChromeModel?.model
                // );
                // For mobile/tablet breakpoints always use the hardcoded modelSmall
                // so a desktop layout saved in the cloud DB never bleeds into mobile.
                const isMobileBreakpoint =
                  breakpoint === 'mobile' ||
                  breakpoint === 'mobileLandscape' ||
                  breakpoint === 'tablet' ||
                  breakpoint === 'tabletLandscape';

                let dryChromeModelToFlexModel = null;
                try {
                  if (isMobileBreakpoint) {
                    dryChromeModelToFlexModel =
                      FlexLayout.Model.fromJson(modelSmall);
                  } else if (activeChromeModel && activeChromeModel.model) {
                    dryChromeModelToFlexModel = FlexLayout.Model.fromJson(
                      injectMissingTabs(activeChromeModel.model)
                    );
                  }
                } catch (error) {
                  logger.error('Error creating FlexLayout model:', error);
                }

                return {
                  activeChromeModel: activeChromeModel,
                  activeFlexLayoutModel: dryChromeModelToFlexModel,
                  breakpoint: event?.breakpoint,
                  chromeModels,
                };
              }),
              sendParent((context, event) => {
                logger.info('SetChromeModelFromChild');
                return {
                  type: 'SetChromeModelFromChild',
                  activeFlexLayoutModel: context?.activeFlexLayoutModel,
                };
              }),
            ],
          },
          GetChromeStateJSON: {},
          GetChromeStateString: {},
        },
      },
      Error: {},
    },
  },
  {
    actions: {
      welcome: (context, event) => {
        logger.info('DryChromeMachine is now ready!');
        return {};
      },
    },
    // setActiveChromeModel: ,
    services: {},
    guards: {
      hasChromeModels: (context, event) => {
        return (
          Boolean(context?.chromeModels?.length) && Boolean(context?.breakpoint)
        );
      },
    },
  }
);
